import { useCallback } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { useSnackbar } from '@ocx-app/lib/snackbar/snackbar';
import { useGetNavigationActions } from '@ocx-app/hooks/useGetNavigationActions';
import { usePaymentMethodSelectorPopup } from '@ocx-app/components/modals';

import { Button } from '@ocx/ui';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useAddPaymentInstrumentPopup } from '@ocx/feat-add-payment-instrument';

import { messages } from '../../pay-at-pump.messages';

const snackbarKey = 'patAtPump:paymentMethodLimitReached:snackbarKey';

export interface IUseHandleAddPaymentMethodClickParams {
  isWalletSizeLimitReached: boolean;
  isGoToWalletButtonShown?: boolean;
}

export const useHandleAddPaymentMethodClick = ({
  isWalletSizeLimitReached,
  isGoToWalletButtonShown = false,
}: IUseHandleAddPaymentMethodClickParams) => {
  const intl = useIntl();
  const snackbar = useSnackbar();
  const { open: openAddPaymentInstrumentPopup } = useAddPaymentInstrumentPopup();
  const { pushToWallet } = useGetNavigationActions();
  const { remove: removePaymentMethodSelectorPopup } = usePaymentMethodSelectorPopup();

  const handleGoToWalletClick = useCallback(() => {
    pushToWallet();
    snackbar.close(snackbarKey);

    // The following code is necessary for the proper functioning of the PayAtPump Payment Selector.
    // Modal does not remove on route change
    // Ticket: https://stuzo.atlassian.net/browse/OCST-7488
    // GitHub Issue: https://github.com/eBay/nice-modal-react/issues/143
    removePaymentMethodSelectorPopup();
  }, [snackbar, pushToWallet, removePaymentMethodSelectorPopup]);

  const showLimitReachedPopup = useCallback(() => {
    snackbar.open(intl.formatMessage(messages.payAtPumpSizeLimitReachedSnackbarTitle), {
      action: isGoToWalletButtonShown ? (
        // TODO: think how to improve or remove. Secondary text button does not exist in DS
        <Button color="secondary" variant="text" onClick={handleGoToWalletClick}>
          <FormattedMessage {...messages.payAtPumpSizeLimitReachedSnackbarButtonText} />
        </Button>
      ) : null,
      key: snackbarKey,
    });
  }, [handleGoToWalletClick, intl, isGoToWalletButtonShown, snackbar]);

  return isWalletSizeLimitReached ? showLimitReachedPopup : openAddPaymentInstrumentPopup;
};
